import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "react-bootstrap";
import api from "services/api";
import emailValidate from "services/emailValidate";
import Translator, { __ } from "services/Translator";
import ModalCloseButton from "components/ModalCloseButton";
import SocialLogin from "components/SocialLogin";
import PasswordPresetForm from "components/loginReg/PasswordPresetForm";
import { AuthBottomWelcomePromo, AuthMobileWelcomeOffer } from "components/AuthWelcomePromo";
import { sanitizeTextInput } from "utils/profileValidation";
import { buildLoginOtpPayload, requestLoginOtp } from "services/authOtp";
import { isPWA } from "utils/isPWA";
import {
  getAuthModel,
  getPresetFields,
  getExplainerImageUrl,
  getEnabledSocialProviders,
} from "utils/signupFlow";

export default function StreamlinedAuthModalBody({
  alertMsg,
  closeLoginModal,
  props,
  loginForm,
  errors,
  updateInput,
  checkInput,
  setSpinner,
  referralCode,
  setReferralCode,
  showReferralField,
  setShowReferralField,
  onOtpRequested,
  handleSignupSuccess,
  activeTab,
  setActiveTab,
  login,
  openRecoverPssModal,
}) {
  const selectedLanguage = Translator.getSelectedLanguage();
  // In the installed PWA the auth screen is a focused, full-screen login/register
  // gate, so the welcome-offer promo panels are omitted (no banners, nothing to
  // scroll past). The browser keeps them unchanged.
  const inPwa = isPWA();
  const [stepOneError, setStepOneError] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [initialSettings, setInitialSettings] = useState({});
  const [ageConfirmation, setAgeConfirmation] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [consentErrors, setConsentErrors] = useState({});

  // Dynamic sign-up flow: the active preset decides which body renders. The
  // bonus dropdown for passwordless lives in the OTP (code-entry) screen, not
  // here. The explainer image is shown for every preset.
  const authModel = getAuthModel(initialSettings);
  const presetFields = getPresetFields(initialSettings);
  const explainerImageUrl = getExplainerImageUrl(initialSettings);
  const isPasswordPreset = authModel === "password";
  const isLoginTab = activeTab === "login";
  // Only show the social panel when at least one social provider is enabled AND
  // configured for this brand (getEnabledSocialProviders is already authoritative
  // on that); otherwise it renders an empty heading.
  const hasSocial = Object.keys(getEnabledSocialProviders(initialSettings)).length > 0;

  // Social sits on TOP of the modal (under the Login/Register tabs, above the
  // email form) for both login and register, and for the passwordless preset.
  const socialSection = hasSocial ? (
    <section className="betmode-auth__panel betmode-auth__panel--social">
      <p className="betmode-auth__social-heading">
        {isLoginTab ? __("Fastest - log in with") : __("Fastest - sign up with")}
      </p>
      <SocialLogin
        callFrom="modal"
        variant="betmode"
        googleButtonId="googleLoginStreamlined"
        referralCode={referralCode || props.referralCode}
        showOrDivider={false}
        {...props}
        initialSettings={initialSettings}
      />
    </section>
  ) : null;

  const emailDivider = hasSocial ? (
    <div className="betmode-auth__email-divider">
      <span>{__("or with email")}</span>
    </div>
  ) : null;

  const getInitialSettings = async () => {
    try {
      const response = await api.get("/initial-settings");
      if (response.status === 200 && response.data) {
        setInitialSettings(response.data);
      }
    } catch (e) {
      // Non-fatal: fall back to legacy defaults (passwordless flow).
    }
  };

  useEffect(() => {
    getInitialSettings();
  }, []);

  useEffect(() => {
    if (props.referralCode) {
      setReferralCode(props.referralCode);
      setShowReferralField(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.referralCode]);

  const handleEmailChange = (ev) => {
    setStepOneError("");
    updateInput(ev);
  };

  const handleStepOneSubmit = async (ev) => {
    ev.preventDefault();
    const email = sanitizeTextInput(loginForm.email, 250).toLowerCase();

    if (!emailValidate(email)) {
      setStepOneError(__("Please enter a valid email address"));
      return;
    }

    const nextConsentErrors = {};
    if (!ageConfirmation) {
      nextConsentErrors.ageConfirmation = __("Please check the age confirmation.");
    }
    if (!agreeToTerms) {
      nextConsentErrors.agreeToTerms = __("Please check the terms and conditions.");
    }
    setConsentErrors(nextConsentErrors);
    if (Object.keys(nextConsentErrors).length) {
      return;
    }

    setIsSendingOtp(true);
    setSpinner(true);
    setStepOneError("");

    try {
      // Bonus is chosen on the code-entry screen now, so it is not sent here.
      const payload = buildLoginOtpPayload({
        email,
        referralCode: referralCode || props.referralCode,
        language: selectedLanguage?.code,
        ageConfirmation,
        agreeToTerms,
      });
      const response = await requestLoginOtp(payload);

      if (response.status === 200) {
        onOtpRequested?.({
          email,
          referralCode: referralCode || props.referralCode || "",
        });
      } else {
        setStepOneError(response.data?.message || __("Unable to send verification code"));
      }
    } catch (err) {
      setStepOneError(err?.message || __("Unable to send verification code"));
    } finally {
      setIsSendingOtp(false);
      setSpinner(false);
    }
  };

  return (
    <div className="betmode-auth">
      <Alert show={alertMsg.showAlert} variant={alertMsg.variant}>
        {alertMsg.message}
      </Alert>

      <div className="betmode-auth__main">
      <section className="betmode-auth__panel betmode-auth__panel--form">
        <ModalCloseButton onClick={closeLoginModal} className="betmode-auth__close modal_close_reg" />

        <div className="betmode-auth__brand">
          <img
            alt="Play365"
            className="betmode-auth__logo"
            src={require("assets/play365/images/logos/logo.webp")}
          />
        </div>

        <h2 className="betmode-auth__title">
          {isPasswordPreset ? (isLoginTab ? __("Login") : __("Register")) : __("Login or Register")}
        </h2>

        {explainerImageUrl ? (
          <div className="betmode-auth__explainer">
            <img src={explainerImageUrl} alt={__("How sign-up works")} className="betmode-auth__explainer-img" />
          </div>
        ) : null}

        {isPasswordPreset ? (
          // Presets 2–5 (password): separate Login and Register, unlike the
          // unified passwordless screen.
          <>
            <div className="betmode-auth__tabs">
              <button
                type="button"
                className={"betmode-auth__tab " + (isLoginTab ? "is-active" : "")}
                onClick={() => setActiveTab && setActiveTab("login")}
              >
                {__("Login")}
              </button>
              <button
                type="button"
                className={"betmode-auth__tab " + (!isLoginTab ? "is-active" : "")}
                onClick={() => setActiveTab && setActiveTab("Sign up")}
              >
                {__("Register")}
              </button>
            </div>

            {socialSection}
            {emailDivider}

            {isLoginTab ? (
              <form className="betmode-auth__form" autoComplete="off" onSubmit={login}>
                <div className="betmode-auth__field">
                  <label className="betmode-auth__label" htmlFor="betmode-login-email">{__("Your email")}</label>
                  <input
                    id="betmode-login-email"
                    type="email"
                    name="email"
                    value={loginForm.email}
                    onChange={updateInput}
                    onBlur={checkInput}
                    autoComplete="email"
                    className={"betmode-auth__input " + (errors.email ? "betmode-auth__input--error" : "")}
                    placeholder={__("Email")}
                  />
                  <div className="betmode-auth__error">{errors.email}</div>
                </div>
                <div className="betmode-auth__field">
                  <label className="betmode-auth__label" htmlFor="betmode-login-pass">{__("Password")}</label>
                  <input
                    id="betmode-login-pass"
                    type="password"
                    name="password"
                    value={loginForm.password}
                    onChange={updateInput}
                    autoComplete="current-password"
                    className={"betmode-auth__input " + (errors.password ? "betmode-auth__input--error" : "")}
                    placeholder={__("Password")}
                  />
                  <div className="betmode-auth__error">{errors.password}</div>
                </div>
                <button type="submit" className="betmode-auth__submit">{__("Log in")}</button>
                {openRecoverPssModal ? (
                  <button
                    type="button"
                    className="betmode-auth__back-link betmode-auth__forgot-link"
                    onClick={openRecoverPssModal}
                  >
                    {__("Forgot password?")}
                  </button>
                ) : null}
                <p className="betmode-auth__switch">
                  {__("No account yet?")}{" "}
                  <button
                    type="button"
                    className="betmode-auth__switch-link"
                    onClick={() => setActiveTab && setActiveTab("Sign up")}
                  >
                    {__("Register")}
                  </button>
                </p>
              </form>
            ) : (
              <>
                <PasswordPresetForm
                  presetFields={presetFields}
                  initialSettings={initialSettings}
                  referralCode={referralCode || props.referralCode}
                  onSuccess={handleSignupSuccess}
                  setSpinner={setSpinner}
                  closeLoginModal={closeLoginModal}
                />
                <p className="betmode-auth__switch">
                  {__("Already have an account?")}{" "}
                  <button
                    type="button"
                    className="betmode-auth__switch-link"
                    onClick={() => setActiveTab && setActiveTab("login")}
                  >
                    {__("Log in")}
                  </button>
                </p>
              </>
            )}
          </>
        ) : (
          // Preset 1: passwordless email-OTP. Bonus selector moved to the code screen.
          <>
          {socialSection}
          {emailDivider}
          <form className="betmode-auth__form" autoComplete="off" onSubmit={handleStepOneSubmit}>
            <div className="betmode-auth__field">
              <label className="betmode-auth__label" htmlFor="betmode-auth-email">
                {__("Your email")}
              </label>
              <input
                id="betmode-auth-email"
                type="email"
                name="email"
                value={loginForm.email}
                onChange={handleEmailChange}
                onBlur={checkInput}
                autoComplete="email"
                className={
                  "betmode-auth__input " + (errors.email || stepOneError ? "betmode-auth__input--error" : "")
                }
                placeholder={__("Email")}
              />
              <div className="betmode-auth__error">{stepOneError || errors.email}</div>
            </div>

            {!showReferralField ? (
              <button
                type="button"
                className="betmode-auth__referral-link"
                onClick={() => setShowReferralField(true)}
              >
                {__("Have referral code? Click here!")}
              </button>
            ) : (
              <div className="betmode-auth__field">
                <input
                  type="text"
                  name="referralCode"
                  value={referralCode}
                  onChange={(ev) => setReferralCode(ev.target.value)}
                  autoComplete="off"
                  className="betmode-auth__input"
                  placeholder={__("Referral code")}
                />
              </div>
            )}

            <div className="betmode-auth__consent">
            <div className="reg_check_area">
              <input
                type="checkbox"
                className="stepTerms"
                id="streamlined-age-confirmation"
                name="ageConfirmation"
                checked={ageConfirmation}
                onChange={(ev) => {
                  setAgeConfirmation(ev.target.checked);
                  setConsentErrors((prev) => ({ ...prev, ageConfirmation: "" }));
                }}
              />
              <label htmlFor="streamlined-age-confirmation" className="check_area">
                <div className="check_check_and_text_area">
                  <div className="check_box">
                    <div className="check_checked_box"></div>
                  </div>
                  <div className="check_text">
                    {__(
                      "I declare that I am 18 years or older, located in a permitted territory and have no self-exclusions."
                    )}
                    <span className="requried_icon">*</span>
                  </div>
                </div>
              </label>
              <div className="errorMsg">{consentErrors.ageConfirmation}</div>
            </div>

            <div className="reg_check_area">
              <input
                type="checkbox"
                className="stepTerms"
                id="streamlined-agree-terms"
                name="agreeToTerms"
                checked={agreeToTerms}
                onChange={(ev) => {
                  setAgreeToTerms(ev.target.checked);
                  setConsentErrors((prev) => ({ ...prev, agreeToTerms: "" }));
                }}
              />
              <label htmlFor="streamlined-agree-terms" className="check_area">
                <div className="check_check_and_text_area">
                  <div className="check_box">
                    <div className="check_checked_box"></div>
                  </div>
                  <div className="check_text">
                    {__("I have read and accept the") + " "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/terms-and-conditions"
                      onClick={closeLoginModal}
                    >
                      {__("Terms of service")}
                    </Link>
                    ,{" "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/privacy-policy"
                      onClick={closeLoginModal}
                    >
                      {__("Privacy policy")}
                    </Link>{" "}
                    {__("and")}{" "}
                    <Link
                      hreflang={selectedLanguage?.code}
                      rel="nofollow noopener noreferrer"
                      to="/responsible-gaming"
                      onClick={closeLoginModal}
                    >
                      {__("Responsible gaming policy")}
                    </Link>
                    .<span className="requried_icon">*</span>
                  </div>
                </div>
              </label>
              <div className="errorMsg">{consentErrors.agreeToTerms}</div>
            </div>
          </div>

          <button type="submit" className="betmode-auth__submit" disabled={isSendingOtp}>
            {isSendingOtp ? __("Sending code…") : __("Continue")}
          </button>
          <p className="betmode-auth__submit-hint">
            {__("We'll email you a 6-digit code to finish - no password needed.")}
          </p>
          </form>
          </>
        )}
      </section>
      </div>

      {!inPwa && props.isMobileDevice ? (
        <section className="betmode-auth__panel betmode-auth__panel--mobile-offer">
          <AuthMobileWelcomeOffer />
        </section>
      ) : null}

      {!inPwa ? (
        <div className="betmode-auth__aside">
          <section className="betmode-auth__panel betmode-auth__panel--promo">
            <AuthBottomWelcomePromo variant="betmode" />
          </section>
        </div>
      ) : null}
    </div>
  );
}
